import { readBrowserRenderCapabilities, resolveRenderProfile } from './render-profile.js';

type InputMode = 'keyboard' | 'pointer';

const root = document.documentElement;
const body = document.body;
const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const reducedTransparencyQuery = window.matchMedia('(prefers-reduced-transparency: reduce)');
const contrastQuery = window.matchMedia('(prefers-contrast: more)');

function syncEnvironment(): void {
  const profile = resolveRenderProfile(readBrowserRenderCapabilities());
  root.dataset.motion = reducedMotionQuery.matches ? 'reduced' : 'full';
  root.dataset.transparency = reducedTransparencyQuery.matches ? 'reduced' : 'full';
  root.dataset.contrast = contrastQuery.matches ? 'more' : 'standard';
  root.dataset.renderQuality = profile.quality;
  root.style.setProperty('--command-viewport-height', `${window.visualViewport?.height ?? window.innerHeight}px`);

  window.dispatchEvent(new CustomEvent('swsg:preferences-change', { detail: { profile } }));
}

function setInputMode(mode: InputMode): void {
  root.dataset.input = mode;
}

function onVisibilityChange(): void {
  root.dataset.documentState = document.hidden ? 'hidden' : 'visible';
  window.dispatchEvent(new CustomEvent('swsg:visibility-change', { detail: { hidden: document.hidden } }));
}

function installRovingTablists(): void {
  const surface = body.dataset.commandSurface || 'command';

  document.querySelectorAll<HTMLElement>('[role="tablist"]').forEach((tablist, tablistIndex) => {
    const tabs = Array.from(tablist.querySelectorAll<HTMLElement>('[role="tab"]'));
    tabs.forEach((tab, tabIndex) => {
      const panelKey = tab.dataset.panelTarget || tab.dataset.wdTool || tab.dataset.workspace || tab.dataset.tab;
      const panelSelector = tab.dataset.panelTarget
        ? `[data-panel="${CSS.escape(panelKey || '')}"]`
        : tab.dataset.wdTool
          ? `[data-wd-tool-panel="${CSS.escape(panelKey || '')}"]`
          : tab.dataset.workspace
            ? `[data-workspace-panel="${CSS.escape(panelKey || '')}"]`
            : `.tab-panel[data-panel="${CSS.escape(panelKey || '')}"]`;
      const panel = panelKey ? document.querySelector<HTMLElement>(panelSelector) : null;

      tab.id ||= `${surface}-tab-${tablistIndex}-${tabIndex}`;
      tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
      if (!panel) return;

      panel.id ||= `${surface}-tabpanel-${tablistIndex}-${tabIndex}`;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      tab.setAttribute('aria-controls', panel.id);
    });

    tablist.addEventListener('keydown', (event) => {
      if (!(event instanceof KeyboardEvent)) return;
      const availableTabs = tabs.filter((tab) => !tab.hidden && !tab.classList.contains('permission-hidden'));
      const currentIndex = availableTabs.indexOf(document.activeElement as HTMLElement);
      if (currentIndex < 0) return;

      let nextIndex: number | null = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % availableTabs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + availableTabs.length) % availableTabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = availableTabs.length - 1;
      if (nextIndex === null) return;

      event.preventDefault();
      availableTabs[nextIndex]?.focus();
      availableTabs[nextIndex]?.click();
    });
  });
}

body.classList.add('command-ready');
setInputMode('pointer');
syncEnvironment();
onVisibilityChange();
installRovingTablists();

window.addEventListener('keydown', (event) => {
  if (event.metaKey || event.ctrlKey || event.altKey) return;
  setInputMode('keyboard');
}, { passive: true });
window.addEventListener('pointerdown', () => setInputMode('pointer'), { passive: true });
window.addEventListener('resize', syncEnvironment, { passive: true });
window.visualViewport?.addEventListener('resize', syncEnvironment, { passive: true });
document.addEventListener('visibilitychange', onVisibilityChange, { passive: true });
reducedMotionQuery.addEventListener('change', syncEnvironment);
reducedTransparencyQuery.addEventListener('change', syncEnvironment);
contrastQuery.addEventListener('change', syncEnvironment);
